import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaArrowLeft,
  FaBox,
  FaCalendarAlt,
  FaCheck,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaEye,
  FaFileAlt,
  FaHandshake,
  FaMapMarkerAlt,
  FaPhone,
  FaShippingFast,
  FaTruck,
  FaUser,
  FaWeightHanging,
} from "react-icons/fa";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { getAvailableRoles } from "../../utils/roleSwitcher";
import "../../assets/styles/logistics_pickup_confirmation.scss";

function safeStr(v, fallback = "—") {
  if (v == null) return fallback;
  const s = String(v).trim();
  return s ? s : fallback;
}

function formatSubmittedAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return safeStr(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatPickupDate(val) {
  if (!val) return "—";
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.length === 10 ? `${s}T12:00:00` : s);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
    }
  }
  return s;
}

function formatPickupTime(val) {
  if (!val) return "—";
  const s = String(val).trim();
  if (/^\d{1,2}:\d{2}/.test(s)) {
    const [h, m] = s.split(":").map((x) => parseInt(x, 10));
    if (Number.isFinite(h) && Number.isFinite(m)) {
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
  }
  return s;
}

function formatWeightKg(val) {
  if (val == null || String(val).trim() === "") return "—";
  const n = Number(String(val).replace(/,/g, ""));
  if (Number.isFinite(n)) return n.toLocaleString();
  return safeStr(val);
}

function formatDisplayText(val) {
  const s = safeStr(val, "");
  if (s === "—") return s;
  return s
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function formatVehicleNo(val) {
  const s = safeStr(val, "");
  if (s === "—") return s;
  return s.toUpperCase();
}

function formatRfqKind(kind) {
  const k = String(kind || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  if (!k) return "—";
  if (k === "generic" || k === "generic-logistics") return "Generic Logistics";
  if (k === "exhibition-logistics") return "Exhibition Logistics";
  if (k === "business-relations") return "Business Relations";
  return k
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatRfqTypeShort(kind) {
  const k = String(kind || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  if (k === "generic" || k === "generic-logistics") return "Generic";
  if (k === "exhibition-logistics") return "Exhibition";
  if (k === "business-relations") return "Business Relations";
  return formatRfqKind(kind);
}

function pickupStatusLabel(status) {
  const s = status != null ? String(status).trim().toLowerCase() : "";
  if (s === "picked_up") return "Picked up";
  if (s === "ready_for_pickup") return "Ready for pickup";
  if (s === "awaiting_pickup") return "Awaiting pickup";
  if (s === "completed") return "Completed";
  return s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";
}

function quoteStatusLabel(status) {
  const s = status != null ? String(status).trim().toLowerCase() : "";
  if (s === "accepted") return "Accepted";
  if (s === "submitted") return "Submitted";
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
}

const WORKFLOW_STEPS = [
  { id: "rfq", label: "RFQ Submitted", Icon: FaClipboardList },
  { id: "quote", label: "Quote Accepted", Icon: FaCheck },
  { id: "handover", label: "Handover", Icon: FaHandshake },
  { id: "pickup", label: "Pickup", Icon: FaTruck },
  { id: "transit", label: "In Transit", Icon: FaShippingFast },
];

function PickupInfoCell({ icon: Icon, label, value }) {
  return (
    <div className="lpc-pickup-cell">
      <div className="lpc-pickup-cell__icon" aria-hidden>
        <Icon />
      </div>
      <div>
        <div className="lpc-pickup-cell__label">{label}</div>
        <div className="lpc-pickup-cell__value">{value}</div>
      </div>
    </div>
  );
}

export default function LogisticsPickupConfirmationPage() {
  const { submissionId, quoteId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!getAvailableRoles().some((r) => r.id === "logistics")) {
      toast.error("This page is only available for logistics providers.");
      navigate("/dashboard/logistics", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sId = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
        const qId = quoteId != null ? parseInt(String(quoteId), 10) : NaN;
        if (!Number.isFinite(sId) || sId <= 0 || !Number.isFinite(qId) || qId <= 0) {
          throw new Error("Invalid pickup link");
        }
        const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_PICKUP_CONTEXT(sId, qId));
        const body = res?.data ?? res ?? {};
        const data = body?.data ?? body;
        if (!cancelled) setCtx(data && typeof data === "object" ? data : null);
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Could not load pickup details.");
        if (!cancelled) setCtx(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId, quoteId]);

  const rfq = ctx?.rfq ?? null;
  const quote = ctx?.quote ?? null;
  const handover = ctx?.handover ?? null;
  const ho = handover?.handover && typeof handover.handover === "object" ? handover.handover : {};
  const pickupStatus = handover?.pickupStatus != null ? String(handover.pickupStatus).trim().toLowerCase() : "";
  const alreadyPickedUp = pickupStatus === "picked_up";

  const summary = useMemo(() => {
    if (!rfq) return null;
    const p = rfq.payload && typeof rfq.payload === "object" ? rfq.payload : {};
    return {
      ref: rfq.rfqReferenceNumber ?? rfq.rfq_reference_number,
      kind: rfq.rfqKind ?? rfq.rfq_kind,
      submittedAt: rfq.submittedAt ?? rfq.submitted_at,
      commodity: p.commodityType || p.cargoDescription || p.materialsDescription || null,
      vehicleType: ho.vehicleType || ho.vehicle_type || p.vehicleType || p.modeDetail || null,
      driverLicense: ho.driverLicense || ho.driverLicenseNumber || ho.licenseNumber || null,
    };
  }, [rfq, ho]);

  const verificationItems = useMemo(() => {
    const hasDriver = Boolean(safeStr(ho.driverName, "") !== "—" && safeStr(ho.driverPhone, "") !== "—");
    const hasVehicle = safeStr(ho.vehicleNumber, "") !== "—";
    const hasSeal = safeStr(ho.sealNumber, "") !== "—";
    const handoverDone = Boolean(handover);
    return [
      { label: "Driver verified", done: hasDriver },
      { label: "Vehicle assigned", done: hasVehicle },
      { label: "Seal verified", done: hasSeal },
      { label: "Buyer handover completed", done: handoverDone },
    ];
  }, [ho, handover]);

  const stepStates = useMemo(() => {
    const states = { rfq: "done", quote: "done", handover: handover ? "done" : "pending", pickup: "pending", transit: "pending" };
    if (handover) {
      states.pickup = alreadyPickedUp ? "done" : "active";
      states.transit = alreadyPickedUp ? "active" : "pending";
    } else {
      states.handover = "active";
      states.pickup = "pending";
    }
    return states;
  }, [handover, alreadyPickedUp]);

  const specialNotes = useMemo(() => {
    const n = ho.notes != null ? String(ho.notes).trim() : "";
    if (n) return n;
    return "Please handle the cargo with care. Verify seal before pickup. Ensure all documents are collected and attached.";
  }, [ho.notes]);

  const stepProgressPct = useMemo(() => {
    if (alreadyPickedUp) return 80;
    if (handover) return 62;
    return 50;
  }, [handover, alreadyPickedUp]);

  const onConfirmPickup = async () => {
    if (alreadyPickedUp || !handover) return;
    const sId = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
    const qId = quoteId != null ? parseInt(String(quoteId), 10) : NaN;
    if (!Number.isFinite(sId) || !Number.isFinite(qId)) return;
    setConfirming(true);
    try {
      await CommonService.post(API_URLS.LOGISTICS_PROVIDER.RFQ_PICKUP_CONFIRM(sId, qId), {
        pickup: {
          confirmedAt: new Date().toISOString(),
          source: "logistics_provider_dashboard",
        },
      });
      toast.success("Pickup confirmed.");
      navigate("/dashboard/logistics/rfqs", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not confirm pickup.");
    } finally {
      setConfirming(false);
    }
  };

  const handoverPath =
    submissionId && quoteId
      ? `/dashboard/logistics/rfqs/${encodeURIComponent(submissionId)}/handover/${encodeURIComponent(quoteId)}`
      : null;

  return (
    <div className="lpc-page">
      <div className="container-fluid py-4">
        <div className="lpc-inner">
          <nav aria-label="breadcrumb" className="lpc-breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/dashboard/logistics">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/dashboard/logistics/rfqs">RFQs received</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Pickup confirmation
              </li>
            </ol>
          </nav>

          <div className="lpc-hero">
            <div>
              <h1 className="lpc-hero__title">Pickup confirmation</h1>
              <p className="lpc-hero__subtitle">
                Review buyer handover details, then confirm when cargo has been collected.
              </p>
            </div>
            <Link to="/dashboard/logistics/rfqs" className="lpc-btn-back">
              <FaArrowLeft aria-hidden />
              Back to RFQs
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !ctx || !rfq ? (
            <div className="alert alert-warning">Unable to load this pickup request.</div>
          ) : (
            <>
              <div className="lpc-stepper-wrap">
                <div className="lpc-stepper" role="list">
                  <div className="lpc-stepper__track" aria-hidden>
                    <div className="lpc-stepper__fill" style={{ width: `${stepProgressPct}%` }} />
                  </div>
                  {WORKFLOW_STEPS.map((step) => {
                    const state = stepStates[step.id] || "pending";
                    const { Icon } = step;
                    return (
                      <div key={step.id} className={`lpc-step lpc-step--${state}`} role="listitem">
                        <div className="lpc-step__circle">
                          {state === "done" ? <FaCheck aria-hidden /> : <Icon aria-hidden />}
                        </div>
                        <div className="lpc-step__label">{step.label}</div>
                        {state === "active" && step.id === "pickup" ? (
                          <div className="lpc-step__current">Current step</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="lpc-stack">
                <div className="lpc-row lpc-row--2col">
                  <div className="lpc-card lpc-card--rfq">
                    <div className="lpc-rfq-head">
                      <div className="lpc-rfq-head__left">
                        <span className="lpc-rfq-head__icon" aria-hidden>
                          <FaFileAlt />
                        </span>
                        <div>
                          <div className="lpc-rfq-head__ref">{safeStr(summary?.ref)}</div>
                          <div className="lpc-rfq-head__kind">{formatRfqKind(summary?.kind)}</div>
                        </div>
                      </div>
                      <span className="lpc-badge lpc-badge--accepted">{quoteStatusLabel(quote?.status)}</span>
                    </div>
                    <div className="lpc-rfq-body">
                      <div className="lpc-rfq-details">
                        <div className="lpc-detail-row">
                          <span className="lpc-detail-row__k">Cargo (from RFQ)</span>
                          <span className="lpc-detail-row__v">{safeStr(summary?.commodity)}</span>
                        </div>
                        <div className="lpc-detail-row">
                          <span className="lpc-detail-row__k">RFQ Type</span>
                          <span className="lpc-detail-row__v">{formatRfqTypeShort(summary?.kind)}</span>
                        </div>
                        <div className="lpc-detail-row">
                          <span className="lpc-detail-row__k">Submitted on</span>
                          <span className="lpc-detail-row__v">{formatSubmittedAt(summary?.submittedAt)}</span>
                        </div>
                      </div>
                      <div className="lpc-rfq-statuses">
                        <div className="lpc-status-line">
                          <span className="lpc-status-line__k">Quote Status</span>
                          <span className="lpc-status-line__v">
                            <span className="lpc-dot lpc-dot--green" aria-hidden />
                            {quoteStatusLabel(quote?.status)}
                          </span>
                        </div>
                        <div className="lpc-status-line">
                          <span className="lpc-status-line__k">Pickup Status</span>
                          <span
                            className={`lpc-status-line__v ${!alreadyPickedUp ? "lpc-status-line__v--awaiting" : ""}`}
                          >
                            <span
                              className={`lpc-dot ${alreadyPickedUp ? "lpc-dot--green" : "lpc-dot--orange"}`}
                              aria-hidden
                            />
                            {pickupStatusLabel(handover?.pickupStatus)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lpc-card">
                    <div className="lpc-card__title">Pickup verification</div>
                    <ul className="lpc-verify-list">
                      {verificationItems.map((item) => (
                        <li key={item.label} className={item.done ? "lpc-verify-list__item--done" : ""}>
                          <span className="lpc-verify-list__icon" aria-hidden>
                            {item.done ? <FaCheckCircle /> : <span className="lpc-verify-list__pending" />}
                          </span>
                          {item.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="lpc-row lpc-row--2col">
                  <div className="lpc-card">
                    <div className="lpc-card__title">Driver &amp; vehicle details</div>
                    <div className="lpc-split">
                      <div className="lpc-split__col lpc-split__col--vehicle">
                        <div className="lpc-split__head">
                          <span className="lpc-split__head-icon lpc-split__head-icon--blue" aria-hidden>
                            <FaTruck />
                          </span>
                          Vehicle Details
                        </div>
                        <div className="lpc-split__rows">
                          <div className="lpc-split__row">
                            <span className="lpc-split__k">Vehicle No.</span>
                            <span className="lpc-split__v">{formatVehicleNo(ho.vehicleNumber)}</span>
                          </div>
                          <div className="lpc-split__row">
                            <span className="lpc-split__k">Seal Number</span>
                            <span className="lpc-split__v">{formatDisplayText(ho.sealNumber)}</span>
                          </div>
                          <div className="lpc-split__row">
                            <span className="lpc-split__k">Vehicle Type</span>
                            <span className="lpc-split__v">{formatDisplayText(summary?.vehicleType)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="lpc-split__col lpc-split__col--driver">
                        <div className="lpc-split__head">
                          <span className="lpc-split__head-icon lpc-split__head-icon--orange" aria-hidden>
                            <FaUser />
                          </span>
                          Driver Details
                        </div>
                        <div className="lpc-split__rows">
                          <div className="lpc-split__row">
                            <span className="lpc-split__k">Driver Name</span>
                            <span className="lpc-split__v">{formatDisplayText(ho.driverName)}</span>
                          </div>
                          <div className="lpc-split__row">
                            <span className="lpc-split__k">Contact Number</span>
                            <span className="lpc-split__v">{safeStr(ho.driverPhone)}</span>
                          </div>
                          <div className="lpc-split__row">
                            <span className="lpc-split__k">License Number</span>
                            <span className="lpc-split__v">{safeStr(summary?.driverLicense)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lpc-card">
                    <div className="lpc-card__title">Pickup information</div>
                    {!handover ? (
                      <p className="text-muted small mb-0 px-1">No buyer handover is recorded for this quote yet.</p>
                    ) : (
                      <div className="lpc-pickup-grid">
                        <PickupInfoCell
                          icon={FaMapMarkerAlt}
                          label="Pickup Location"
                          value={formatDisplayText(ho.handoverLocation)}
                        />
                        <PickupInfoCell icon={FaCalendarAlt} label="Pickup Date" value={formatPickupDate(ho.pickupDate)} />
                        <PickupInfoCell icon={FaClock} label="Pickup Time" value={formatPickupTime(ho.pickupTime)} />
                        <PickupInfoCell icon={FaBox} label="Packages" value={safeStr(ho.numberOfPackages)} />
                        <PickupInfoCell icon={FaWeightHanging} label="Weight (KG)" value={formatWeightKg(ho.actualWeightKg)} />
                        <PickupInfoCell
                          icon={FaPhone}
                          label="Contact"
                          value={`${formatDisplayText(ho.contactPersonName)}${safeStr(ho.contactPhone) !== "—" ? `, ${ho.contactPhone}` : ""}`}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="lpc-notes">
                  <div className="lpc-notes__icon" aria-hidden>
                    <FaFileAlt />
                  </div>
                  <div>
                    <div className="lpc-notes__title">Special Instructions / Notes</div>
                    <p className="lpc-notes__text mb-0">{specialNotes}</p>
                  </div>
                </div>
              </div>

              <div className="lpc-footer-actions">
                {handoverPath ? (
                  <Link to={handoverPath} state={{ from: "pickup-confirmation" }} className="lpc-btn-outline">
                    <FaEye aria-hidden />
                    View Handover
                  </Link>
                ) : null}
                <button
                  type="button"
                  className="lpc-btn-confirm"
                  disabled={!handover || alreadyPickedUp || confirming}
                  onClick={onConfirmPickup}
                >
                  <FaCheck aria-hidden />
                  {alreadyPickedUp ? "Already picked up" : confirming ? "Confirming…" : "Confirm Pickup"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
